import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../axiosInstance";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import CustomTooltip from "../components/generic/CustomTooltip";
import PodCard from "../components/pods/podCard";
import PodInfo from "../components/pods/podInfo";
import { useEnv } from "../context/EnvContext";

const Operations = () => {
  const [pods, setPods] = useState([]); // State to store pod data
  const [showPodInfo, setShowPodInfo] = useState(false); // State to control visibility
  const [cluster, setCluster] = useState(""); // State to control selected cluster
  const [dataLoaded, setDataLoaded] = useState(false); // State to control data loading
  const [selectedPod, setSelectedPod] = useState(null); // State to control selected pod
  const [isClusterDetermined, setIsClusterDetermined] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const envContext = useEnv();
  const env = envContext?.env;

  // retrieve pod data from the server
  const getPods = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `/get-pods-info?cluster=${cluster}&namespace=238240-${env}`
      );
      setPods(response.data);
      setInitialDataLoaded(true);
      setDataLoaded(true);
      console.log("Pods obtained from server:\n", response.data);
      if (typeof envContext?.setIsLoaded === "function") {
        envContext.setIsLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching pod data:", error);
    }
  }, [cluster, env, envContext]);

  // useEffect to determine the cluster based on the environment and URL
  useEffect(() => {
    if (!env) return;

    const url = window.location.href;
    if (env === "prod") {
      setCluster(url.includes("milford") ? "prodM" : "prodW");
    } else {
      setCluster("dev");
    }
    setIsClusterDetermined(true);
  }, [env]);

  // useEffect to fetch pod data initially
  useEffect(() => {
    if (!env || !cluster) return;

    console.log("Current environment: ", env);
    console.log("Selected Cluster: ", cluster);
    getPods();
  }, [env, cluster, getPods]);

  // Separate useEffect for auto-refresh that only runs after initial load
  useEffect(() => {
    if (!initialDataLoaded || !env || !cluster) return;

    console.log("Setting up auto-refresh interval...");
    const intervalId = setInterval(() => {
      console.log("Auto-refreshing pod data...");
      getPods();
    }, 15000);

    return () => {
      console.log("Cleaning up auto-refresh interval...");
      clearInterval(intervalId);
    };
  }, [initialDataLoaded, env, cluster, getPods]);

  // Simplified loading check - render as soon as we have pods
  if (!isClusterDetermined || (!initialDataLoaded && pods.length === 0)) {
    return (
      <Container
        fluid
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <span>Loading pods...</span>
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  const handlePodSelect = (pod) => {
    if (selectedPod && selectedPod.pod_name === pod.pod_name) {
      // Clicking the same pod again - hide the info
      setShowPodInfo(false);
      setSelectedPod(null);
    } else {
      // Clicking a different pod - show its info
      setSelectedPod(pod);
      setShowPodInfo(true);
    }
  };

  // WIP --- Sort Node Names based on name
  // const sortNodeNames = (a, b) => {
  //   const nodeName1 = a[0];
  //   const nodeName2 = b[0];
  //   if (nodeName1 === 'N/A') return -1;
  //   if (nodeName2 === 'N/A') return 1;
  //   return nodeName1.localeCompare(nodeName2);
  // };

  // Group pods by node
  const podsByNode = pods.reduce((groups, pod) => {
    const nodeName = pod.node_info.name;
    if (!groups[nodeName]) {
      groups[nodeName] = [];
    }
    groups[nodeName].push(pod);
    return groups;
  }, {});

  console.log("Pods grouped by node:\n", podsByNode);

  const formatCpu = (cpuValue) => {
    if (!cpuValue) return "0";
    // Remove 'n' and convert to number
    const numericValue = parseInt(cpuValue.replace("n", ""));
    // Convert nanocores to millicores (more commonly used in K8s)
    const millicores = (numericValue / 1000).toFixed(2);
    return `${millicores} m`;
  };

  const formatMemory = (memoryValue) => {
    if (!memoryValue) return "0";
    const kibibytes = parseInt(memoryValue.replace("Ki", ""));

    if (kibibytes >= 1048576) {
      // 1 GiB = 1048576 KiB
      return `${(kibibytes / 1048576).toFixed(2)} GiB`;
    } else if (kibibytes >= 1024) {
      // 1 MiB = 1024 KiB
      return `${(kibibytes / 1024).toFixed(2)} MiB`;
    } else {
      return `${kibibytes}Kib`;
    }
  };

  return (
    <>
      <Container fluid className="title text-center">
        <h2
          style={{
            fontSize: "2rem",
            margin: "0.5rem 0",
          }}
        >
          Pod Management:{" "}
          {cluster === "prodM"
            ? "Prod Milford"
            : cluster === "prodW"
            ? "Prod Warren"
            : "Dev Milford"}
        </h2>
      </Container>
      <Container fluid className="pod p-4">
        <Row>
          {/* Main content column */}
          <Col
            md={showPodInfo ? 8 : 12}
            style={{
              transition: "width 0.3s ease",
              paddingRight: showPodInfo ? "15px" : "0",
            }}
          >
            {Object.entries(podsByNode)
              //.sort(sortNodeNames)
              .map(([nodeName, nodePods]) => {
                const nodeInfo = nodePods[0].node_info;
                return (
                  <Container
                    key={nodeName}
                    className="node-container position-relative"
                    style={{
                      border: "1px solid #ccc",
                      padding: "16px",
                      borderRadius: "8px",
                      marginTop: "10px",
                      marginBottom: "50px",
                    }}
                  >
                    <div
                      className="node-title d-flex justify-content-between align-items-center position-absolute w-100"
                      style={{ top: "-20px", left: "0" }}
                    >
                      <div
                        className="node"
                        style={{
                          backgroundColor: "#0072ce",
                          color: "#fff",
                          padding: "6px 20px",
                          borderRadius: "16px",
                          fontSize: "1.25rem",
                          fontWeight: "500",
                          minWidth: "50px",
                          textAlign: "center",
                          transition: "opacity 0.2s",
                          cursor: "pointer",
                          margin: "0px 25px",
                        }}
                      >
                        Node: {nodeName || "Unknown"}
                      </div>

                      <div
                        className="node-info d-flex gap-4 me-3 align-items-center"
                        style={{
                          backgroundColor: "#f8f9fa",
                          padding: "8px",
                          borderRadius: "8px",
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: "8px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {/* CPU */}
                        <CustomTooltip content={<span>CPU Allocation</span>}>
                          <div className="d-flex align-items-center">
                            <i
                              className="fa-solid fa-server"
                              style={{ marginRight: "4px", color: "blue" }}
                            ></i>
                            {nodeInfo.allocatable.cpu
                              ? formatCpu(nodeInfo.allocatable.cpu)
                              : "N/A"}
                          </div>
                        </CustomTooltip>
                        {/* Memory */}
                        <CustomTooltip content={<span>RAM Allocation</span>}>
                          <div className="d-flex align-items-center">
                            <i
                              className="fa-solid fa-microchip"
                              style={{ marginRight: "4px", color: "#FFC107" }}
                            ></i>
                            {nodeInfo.allocatable.memory
                              ? formatMemory(nodeInfo.allocatable.memory)
                              : "N/A"}
                          </div>
                        </CustomTooltip>
                        {/* GPU */}
                        <CustomTooltip content={<span>GPU Allocation</span>}>
                          <div className="d-flex align-items-center">
                            <i
                              className="fa-solid fa-desktop"
                              style={{ marginRight: "4px", color: "green" }}
                            ></i>
                            {parseInt(nodeInfo.allocatable["nvidia.com/gpu"]) ||
                              "N/A"}{" "}
                            GPUs
                          </div>
                        </CustomTooltip>
                      </div>
                    </div>
                    <Row xs={1} sm={1} md={2} lg={3} className="g-4">
                      {nodePods.map((pod, index) => (
                        <Col
                          key={index}
                          style={{ cursor: "pointer", marginTop: "50px" }}
                        >
                          <PodCard
                            setShowPodInfo={(show) => handlePodSelect(pod)}
                            podInfo={pod}
                            dataLoaded={dataLoaded}
                            isSelected={
                              selectedPod &&
                              selectedPod.pod_name === pod.pod_name
                            }
                          />
                        </Col>
                      ))}
                    </Row>
                  </Container>
                );
              })}
          </Col>

          {/* PodInfo sidebar */}
          {showPodInfo && selectedPod && (
            <Col
              md={4}
              className="podInfo"
              style={{
                position: "sticky",
                top: "30px",
                backgroundColor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "20px",
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                marginTop: "10px",
                marginBottom: "50px",
                height: "fit-content",
                maxHeight: "calc(100vh - 100px)",
                overflowY: "auto",
              }}
            >
              <PodInfo
                setShowPodInfo={setShowPodInfo}
                podInfo={selectedPod}
                dataLoaded={dataLoaded}
                cluster={cluster}
                refreshPodData={getPods}
              />
            </Col>
          )}
        </Row>
      </Container>
    </>
  );
};

export default Operations;
